import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LogOut, Menu } from "lucide-react";
import { useState } from "react";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { Brand } from "./common";
import { NotificationBell } from "./NotificationBell";
import { useAuth } from "../context/AuthContext";

const NavItems = ({ items, onClick }) => (
  <nav className="space-y-1">
    {items.map(({ to, label, icon: Icon, end }) => (
      <NavLink key={to} to={to} end={end} onClick={onClick} data-testid={`nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
        className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-emerald-500/15 text-emerald-300" : "text-slate-300 hover:bg-slate-800 hover:text-white"}`}>
        <Icon className="h-5 w-5 shrink-0" />{label}
      </NavLink>
    ))}
  </nav>
);

export const AppShell = ({ items, bottomItems, header, roleLabel }) => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const doLogout = async () => { await logout(); nav("/login"); };
  const Side = ({ onClick }) => (
    <div className="flex h-full flex-col bg-[#0B192C] p-4">
      <Brand light className="px-2 py-2" />
      <p className="mt-1 px-2 text-[11px] font-semibold uppercase tracking-widest text-slate-500">{roleLabel}</p>
      <div className="mt-6 flex-1 overflow-y-auto"><NavItems items={items} onClick={onClick} /></div>
      <div className="border-t border-slate-800 pt-4">
        <p className="truncate px-3 text-sm font-medium text-white">{user?.name}</p>
        <p className="truncate px-3 text-xs text-slate-400">{user?.email}</p>
        <button onClick={doLogout} data-testid="logout-button" className="mt-3 flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-300 hover:bg-slate-800 hover:text-white">
          <LogOut className="h-5 w-5" /> Logout
        </button>
      </div>
    </div>
  );
  return (
    <div className="min-h-screen bg-slate-50">
      <aside className="fixed inset-y-0 left-0 hidden w-64 lg:block"><Side /></aside>
      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between gap-3 border-b border-slate-200 bg-white/95 px-4 backdrop-blur sm:px-6">
          <div className="flex items-center gap-2">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button className="rounded-lg p-2 text-slate-700 hover:bg-slate-100 lg:hidden" data-testid="mobile-menu-button"><Menu className="h-6 w-6" /></button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 border-0 p-0"><Side onClick={() => setOpen(false)} /></SheetContent>
            </Sheet>
            <div className="lg:hidden"><Brand /></div>
          </div>
          <div className="flex items-center gap-2">{header}<NotificationBell /></div>
        </header>
        <main className={`mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:py-8 ${bottomItems ? "pb-28 lg:pb-8" : ""}`}><Outlet /></main>
      </div>
      {bottomItems && (
        <nav className="fixed inset-x-0 bottom-0 z-30 grid grid-cols-5 border-t border-slate-200 bg-white lg:hidden" data-testid="bottom-nav">
          {bottomItems.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} data-testid={`bottom-nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) => `flex flex-col items-center gap-1 py-2.5 text-[11px] font-medium ${isActive ? "text-emerald-600" : "text-slate-500"}`}>
              <Icon className="h-5 w-5" />{label}
            </NavLink>
          ))}
        </nav>
      )}
    </div>
  );
};
